import { OverdueAssessmentMeta, isAssessmentOverdue } from '@equip.health/ui';
import { DateTime } from 'luxon';

import { CategorizedAssessments } from '~/lib/types/survey/CategorizedAssessments';

import { SurveyResponseStatus } from '../constants/survey/SurveyResponseStatus';
import { GroupedAssessment } from '../types/survey/GroupedAssessment';
import { formatDaysToReadableString } from '../utils';

export const categorizeAndSortAssessments = (
  groupedAssessments: GroupedAssessment[],
  enableCoreGrouping = true,
): CategorizedAssessments => {
  const coreAssessments = enableCoreGrouping
    ? groupedAssessments.filter(
        (ga) => ga.category.name === 'Patient' && ga.isCore,
      )
    : [];

  // Categorize all assessments using existing logic
  const categorizedAssessments = groupedAssessments.reduce(
    (acc, groupedAssessment) => {
      const { category, subCategory } = groupedAssessment;
      const categoryName = category.name;
      const subCategoryName = subCategory.name;

      acc[categoryName] = acc[categoryName] || {};
      acc[categoryName][subCategoryName] = acc[categoryName][
        subCategoryName
      ] || {
        assessments: [],
      };
      acc[categoryName][subCategoryName].assessments.push(groupedAssessment);

      return acc;
    },
    {} as CategorizedAssessments,
  );

  const sortedCategorizedAssessments: CategorizedAssessments = {};

  // Sort and add all categories
  Object.keys(categorizedAssessments)
    .sort((a, b) => {
      const categoryA =
        groupedAssessments.find((ga) => ga.category.name === a)?.category
          .priority ?? 0;
      const categoryB =
        groupedAssessments.find((ga) => ga.category.name === b)?.category
          .priority ?? 0;
      return categoryA - categoryB;
    })
    .forEach((categoryName) => {
      const subcategoryMap: CategorizedAssessments[string] = {};

      // For Patient category, add Core Clinical Assessments first if any exist
      if (categoryName === 'Patient' && coreAssessments.length > 0) {
        subcategoryMap['Core Clinical Assessments'] = {
          assessments: coreAssessments.sort((a, b) => a.priority - b.priority),
        };
      }

      // Add all other subcategories sorted by priority
      Object.keys(categorizedAssessments[categoryName])
        .sort((a, b) => {
          const subCategoryA =
            groupedAssessments.find((ga) => ga.subCategory.name === a)
              ?.subCategory.priority ?? 0;
          const subCategoryB =
            groupedAssessments.find((ga) => ga.subCategory.name === b)
              ?.subCategory.priority ?? 0;
          return subCategoryA - subCategoryB;
        })
        .forEach((subCategoryName) => {
          const filteredAssessments = categorizedAssessments[categoryName][
            subCategoryName
          ].assessments.filter(
            (a) =>
              !enableCoreGrouping || categoryName !== 'Patient' || !a.isCore,
          );

          if (filteredAssessments.length > 0) {
            subcategoryMap[subCategoryName] = {
              assessments: filteredAssessments.sort(
                (a, b) => a.priority - b.priority,
              ),
            };
          }
        });

      sortedCategorizedAssessments[categoryName] = subcategoryMap;
    });

  return sortedCategorizedAssessments;
};

export const sortCategorizedAssessmentsByAssessmentLength = (
  categorizedAssessments: CategorizedAssessments,
): CategorizedAssessments => {
  const sortedCategorizedAssessments: CategorizedAssessments = {};

  Object.keys(categorizedAssessments).forEach((categoryName) => {
    sortedCategorizedAssessments[categoryName] = {};

    Object.keys(categorizedAssessments[categoryName]).forEach(
      (subCategoryName) => {
        const assessmentsCopy = [
          ...categorizedAssessments[categoryName][subCategoryName].assessments,
        ];

        const sortedAssessments = assessmentsCopy.sort((a, b) => {
          if (a.assessments.length === 0 && b.assessments.length > 0) {
            return 1;
          }
          if (a.assessments.length > 0 && b.assessments.length === 0) {
            return -1;
          }
          return a.priority - b.priority;
        });

        sortedCategorizedAssessments[categoryName][subCategoryName] = {
          assessments: sortedAssessments,
        };
      },
    );
  });

  return sortedCategorizedAssessments;
};

export const extractSortedAssessments = (
  categorizedAssessments: CategorizedAssessments,
): GroupedAssessment[] => {
  return Object.values(categorizedAssessments).reduce<GroupedAssessment[]>(
    (acc, subCategories) => {
      Object.values(subCategories).forEach((subcategory) => {
        acc.push(...subcategory.assessments);
      });
      return acc;
    },
    [],
  );
};

export const collectOverdueAssessments = (
  categorizedAssessments: CategorizedAssessments,
): OverdueAssessmentMeta[] => {
  const overdueAssessments = [];
  Object.entries(categorizedAssessments).forEach(
    ([categoryName, subCategoryData]) => {
      const subCategories = [];
      Object.entries(subCategoryData).forEach(
        ([subCategoryName, { assessments }]) => {
          const assessmentMap = new Map();
          assessments.forEach((assessment: GroupedAssessment) => {
            const { jsonParameters, displayName, externalId } = assessment;
            const parsedFrequency = JSON.parse(jsonParameters).frequency;
            if (assessment.assessments.length > 0) {
              const { status, createdDate } =
                assessment?.assessments?.[0] ?? {};

              if (
                status === SurveyResponseStatus.NEW ||
                status === SurveyResponseStatus.IN_PROGRESS ||
                status === SurveyResponseStatus.REQUESTED
              ) {
                const createdDateEpoch =
                  DateTime.fromISO(createdDate).toMillis();
                const overdueBy = formatDaysToReadableString(
                  DateTime.fromMillis(createdDateEpoch).diffNow('days').days,
                );
                if (isAssessmentOverdue(createdDateEpoch, parsedFrequency)) {
                  assessmentMap.set(externalId, {
                    externalId,
                    name: displayName,
                    overdueBy,
                  });
                }
              }
            }
          });

          if (assessmentMap.size > 0) {
            subCategories.push({
              subCategoryName,
              assessments: Array.from(assessmentMap.values()),
            });
          }
        },
      );

      if (subCategories.length > 0) {
        overdueAssessments.push({
          categoryName,
          subCategories,
        });
      }
    },
  );
  return overdueAssessments;
};
